// =============================================
// Parent Calendar Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  Plus,
  Thermometer,
  Plane,
  X,
  Calendar as CalendarIcon,
  AlertCircle,
  Pencil,
  Trash2,
  Loader2,
  RotateCcw
} from 'lucide-react';
import { childCalendarApi, ChildAbsence, parentApi, ParentCalendarEventDto } from '../../services/api';
import { parentColors as colors } from '../../styles/colors';
import { HEBREW_DAYS as hebrewDays, HEBREW_MONTHS as hebrewMonths } from '../../utils/hebrewCalendar';

// System calendar event type colors
const SYSTEM_EVENT_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  1: { bg: '#fef2f2', text: '#ef4444', label: 'חג' },
  2: { bg: '#fef3c7', text: '#f59e0b', label: 'חול המועד' },
  3: { bg: '#dbeafe', text: '#3b82f6', label: 'חופשה' },
  5: { bg: '#f5f3ff', text: '#8b5cf6', label: 'אחר' },
};

// Types
interface Absence {
  publicId?: string; // From server (using publicId instead of internal Id for security)
  date: string;
  childName: string;
  childPublicId?: string;
  reason: 'sick' | 'vacation' | 'other';
  notes?: string;
}

// Helper to convert API reason (1,2,3) to string
const reasonToString = (reason: number): 'sick' | 'vacation' | 'other' => {
  if (reason === 1) return 'vacation';
  if (reason === 2) return 'sick';
  return 'other';
};

// Helper to convert string reason to API reason (1,2,3)
const stringToReason = (reason: 'sick' | 'vacation' | 'other'): number => {
  if (reason === 'vacation') return 1;
  if (reason === 'sick') return 2;
  return 3;
};

// Convert API absences to local format (expand date ranges)
const convertApiAbsencesToLocal = (apiAbsences: ChildAbsence[]): Absence[] => {
  const localAbsences: Absence[] = [];

  for (const apiAbs of apiAbsences) {
    const startDate = new Date(apiAbs.startDate);
    const endDate = new Date(apiAbs.endDate);

    // Generate an absence for each day in the range
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      localAbsences.push({
        publicId: apiAbs.publicId,
        date: date.toISOString().split('T')[0],
        childName: apiAbs.childName,
        childPublicId: apiAbs.childPublicId,
        reason: reasonToString(apiAbs.reason),
        notes: apiAbs.notes,
      });
    }
  }

  return localAbsences;
};

// קומפוננטת תאריך מותאמת - הקלדה ידנית בפורמט dd/MM/yyyy + לוח שנה נטיבי
function DateInput({
  value,
  onChange,
  min,
  borderColor,
}: {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  min?: string;
  borderColor?: string;
}) {
  const hiddenDateRef = useRef<HTMLInputElement>(null);

  // המרה מ-YYYY-MM-DD ל-dd/MM/yyyy להצגה
  const toDisplay = (isoDate: string): string => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}/${m}/${y}`;
  };

  const [displayValue, setDisplayValue] = useState(toDisplay(value));

  // סנכרון כשהערך חיצוני משתנה
  useEffect(() => {
    setDisplayValue(toDisplay(value));
  }, [value]);

  // ניסיון להמיר טקסט שהוקלד ל-YYYY-MM-DD
  const parseInput = (text: string): string | null => {
    // ניקוי - תמיכה ב-dd/MM/yyyy, dd.MM.yyyy, dd-MM-yyyy
    const cleaned = text.replace(/[.\-]/g, '/');
    const parts = cleaned.split('/');
    if (parts.length !== 3) return null;

    const [d, m, y] = parts;
    const day = parseInt(d, 10);
    const month = parseInt(m, 10);
    const year = parseInt(y, 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2020 || year > 2030) return null;

    // בדיקה שהתאריך תקין
    const dateObj = new Date(year, month - 1, day);
    if (dateObj.getDate() !== day || dateObj.getMonth() !== month - 1 || dateObj.getFullYear() !== year) return null;

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleTextChange = (text: string) => {
    // הוספת / אוטומטית אחרי יום וחודש
    let formatted = text;
    const digitsOnly = text.replace(/\D/g, '');

    if (digitsOnly.length >= 2 && text.length === 2 && !text.includes('/')) {
      formatted = digitsOnly.slice(0, 2) + '/';
    } else if (digitsOnly.length >= 4 && text.split('/').length === 2 && text.endsWith(text.split('/')[1]) && text.split('/')[1].length === 2) {
      formatted = digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2, 4) + '/';
    }

    // הגבלה ל-10 תווים (dd/MM/yyyy)
    if (formatted.length > 10) formatted = formatted.slice(0, 10);

    setDisplayValue(formatted);

    // ניסיון המרה
    const iso = parseInput(formatted);
    if (iso) {
      // בדיקת min
      if (min && iso < min) return;
      onChange(iso);
    }
  };

  const handleBlur = () => {
    // בעזיבת השדה, אם לא נמצא תאריך תקין - חזור לערך הקודם
    const iso = parseInput(displayValue);
    if (!iso) {
      setDisplayValue(toDisplay(value));
    }
  };

  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue) {
      onChange(newValue);
      setDisplayValue(toDisplay(newValue));
    }
  };

  const openDatePicker = () => {
    if (hiddenDateRef.current) {
      hiddenDateRef.current.showPicker?.();
      hiddenDateRef.current.focus();
      hiddenDateRef.current.click();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder="dd/MM/yyyy"
        value={displayValue}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
        style={{
          width: '100%',
          padding: '10px',
          paddingLeft: '40px',
          borderRadius: '10px',
          border: `2px solid ${borderColor || colors.border}`,
          fontSize: '14px',
          color: colors.text,
          backgroundColor: 'white',
          boxSizing: 'border-box',
          textAlign: 'right',
        }}
      />
      {/* כפתור לפתיחת לוח שנה נטיבי */}
      <button
        type="button"
        onClick={openDatePicker}
        style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CalendarIcon size={18} color={colors.textLight} />
      </button>
      {/* input נסתר לפתיחת date picker נטיבי */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={value}
        min={min}
        onChange={handleNativeDateChange}
        style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0,
          width: '30px',
          height: '30px',
          cursor: 'pointer',
          pointerEvents: 'none',
        }}
        tabIndex={-1}
      />
    </div>
  );
}

// הוק לזיהוי גודל מסך - טלפון מול מחשב/טאבלט
function useIsDesktop(breakpoint = 640) {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= breakpoint);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isDesktop;
}

export default function ParentCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [apiAbsences, setApiAbsences] = useState<ChildAbsence[]>([]); // Store original API data
  const [selectedDayInfo, setSelectedDayInfo] = useState<{ day: number; absences: Absence[] } | null>(null);
  const [editingAbsence, setEditingAbsence] = useState<Absence | null>(null);
  // For editing a range of absences (same child, consecutive dates)
  const [editingAbsenceRange, setEditingAbsenceRange] = useState<Absence[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [children, setChildren] = useState<{ name: string; publicId: string; relationType: string }[]>([]);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<ParentCalendarEventDto[]>([]);
  const isDesktop = useIsDesktop();

  // Load children from API only (not localStorage - stale data can cause permission mismatches)
  const loadChildren = useCallback(async () => {
    try {
      console.log('Loading children from API...');
      const response = await parentApi.getMyChildren();
      console.log('Children API response:', response);
      if (response.success && response.data && response.data.length > 0) {
        const childrenData = response.data.map(c => ({
          name: c.childFirstName,
          publicId: c.childPublicId,
          relationType: c.relationType,
        }));
        setChildren(childrenData);
        console.log('Children loaded from API:', childrenData);
      } else {
        console.log('API returned no children');
        setChildren([]);
      }
    } catch (err) {
      console.error('Error loading children from API:', err);
      setChildren([]);
    } finally {
      setChildrenLoaded(true);
    }
  }, []);

  // Load absences from API
  const loadAbsences = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get a wide date range for the calendar
      const fromDate = new Date(currentDate.getFullYear() - 1, 0, 1);
      const toDate = new Date(currentDate.getFullYear() + 1, 11, 31);

      const response = await childCalendarApi.getAbsences(
        fromDate.toISOString().split('T')[0],
        toDate.toISOString().split('T')[0]
      );

      if (response.success && response.data) {
        setApiAbsences(response.data);
        setAbsences(convertApiAbsencesToLocal(response.data));
      } else {
        setError(response.message || 'שגיאה בטעינת נתונים');
      }
    } catch (err) {
      console.error('Error loading absences:', err);
      setError('שגיאה בטעינת נתונים מהשרת');
    } finally {
      setIsLoading(false);
    }
  }, [currentDate]);

  // Load children on mount
  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  // Load calendar events (system + KG overrides)
  const loadCalendarEvents = useCallback(async () => {
    try {
      const response = await parentApi.getCalendarEvents(currentDate.getFullYear());
      if (response.success && response.data) {
        setCalendarEvents(response.data);
      }
    } catch (err) {
      console.error('Error loading calendar events:', err);
    }
  }, [currentDate]);

  // Load data on mount and when year changes
  useEffect(() => {
    loadAbsences();
    loadCalendarEvents();
  }, [loadAbsences, loadCalendarEvents]);

  // Check if child has existing absences in a date range
  const checkExistingAbsences = (childName: string, dates: string[]): Absence[] => {
    return absences.filter(a => a.childName === childName && dates.includes(a.date));
  };

  // Function to add new absence(s) via API - returns error if conflicts exist
  const addAbsence = async (newAbsences: Absence[]): Promise<{ success: boolean; conflicts?: Absence[]; error?: string }> => {
    // Check for local conflicts first
    const conflicts: Absence[] = [];

    for (const newAbs of newAbsences) {
      const existing = absences.find(
        a => a.date === newAbs.date && a.childName === newAbs.childName
      );
      if (existing) {
        conflicts.push(existing);
      }
    }

    if (conflicts.length > 0) {
      return { success: false, conflicts };
    }

    // Group absences by child and find date ranges
    const absencesByChild = new Map<string, Absence[]>();
    for (const abs of newAbsences) {
      const key = abs.childPublicId || abs.childName;
      if (!absencesByChild.has(key)) {
        absencesByChild.set(key, []);
      }
      absencesByChild.get(key)!.push(abs);
    }

    try {
      // Create API requests for each child's absences
      for (const [childKey, childAbsences] of absencesByChild) {
        const sortedDates = childAbsences.map(a => a.date).sort();
        const startDate = sortedDates[0];
        const endDate = sortedDates[sortedDates.length - 1];
        const firstAbs = childAbsences[0];

        const response = await childCalendarApi.createAbsence({
          childPublicId: firstAbs.childPublicId || '',
          startDate,
          endDate,
          reason: stringToReason(firstAbs.reason),
          notes: firstAbs.notes,
        });

        if (!response.success) {
          // If permission error for a specific child, remove from children list
          if (response.message?.includes('הרשאה')) {
            const childName = childAbsences[0]?.childName;
            setChildren(prev => prev.filter(c => c.publicId !== childKey));
            return { success: false, error: `אין לך הרשאה לדווח עבור ${childName || 'הילד'}. הילד הוסר מהרשימה.` };
          }
          return { success: false, error: response.message || 'שגיאה בשמירת היעדרות' };
        }
      }

      // Reload data from server
      await loadAbsences();
      return { success: true };
    } catch (err: unknown) {
      console.error('Error creating absence:', err);
      // Try to extract error message from server response
      const axiosError = err as { response?: { data?: { message?: string } } };
      const serverMessage = axiosError?.response?.data?.message;
      return { success: false, error: serverMessage || 'שגיאה בשמירת היעדרות לשרת' };
    }
  };

  // Function to update an existing absence via API
  const updateAbsence = async (originalAbsence: Absence, updatedAbsence: Absence): Promise<{ success: boolean; error?: string }> => {
    if (!originalAbsence.publicId) {
      return { success: false, error: 'לא נמצא מזהה להיעדרות' };
    }

    try {
      const response = await childCalendarApi.updateAbsence(originalAbsence.publicId, {
        startDate: updatedAbsence.date,
        endDate: updatedAbsence.date,
        reason: stringToReason(updatedAbsence.reason),
        notes: updatedAbsence.notes,
      });

      if (response.success) {
        await loadAbsences();
        setSelectedDayInfo(null);
        setEditingAbsence(null);
        setEditingAbsenceRange(null);
        return { success: true };
      } else {
        return { success: false, error: response.message };
      }
    } catch (err) {
      console.error('Error updating absence:', err);
      return { success: false, error: 'שגיאה בעדכון היעדרות' };
    }
  };

  // Function to delete a single absence via API
  const deleteAbsence = async (absenceToDelete: Absence): Promise<{ success: boolean; error?: string }> => {
    if (!absenceToDelete.publicId) {
      return { success: false, error: 'לא נמצא מזהה להיעדרות' };
    }

    try {
      // Find the original API absence to check if this is part of a range
      const apiAbsence = apiAbsences.find(a => a.publicId === absenceToDelete.publicId);

      if (apiAbsence && apiAbsence.startDate !== apiAbsence.endDate) {
        // It's part of a range - delete just this day
        const response = await childCalendarApi.deleteAbsence(absenceToDelete.publicId, {
          singleDate: absenceToDelete.date,
        });

        if (!response.success) {
          return { success: false, error: response.message };
        }
      } else {
        // Single day - delete entirely
        const response = await childCalendarApi.deleteAbsence(absenceToDelete.publicId, {
          deleteAll: true,
        });

        if (!response.success) {
          return { success: false, error: response.message };
        }
      }

      await loadAbsences();
      setSelectedDayInfo(null);
      return { success: true };
    } catch (err) {
      console.error('Error deleting absence:', err);
      return { success: false, error: 'שגיאה במחיקת היעדרות' };
    }
  };

  // Function to delete multiple absences (a range) via API
  const deleteAbsenceRange = async (absencesToDelete: Absence[]): Promise<{ success: boolean; error?: string }> => {
    if (absencesToDelete.length === 0) {
      return { success: false, error: 'אין היעדרויות למחיקה' };
    }

    const firstAbsence = absencesToDelete[0];
    if (!firstAbsence.publicId) {
      return { success: false, error: 'לא נמצא מזהה להיעדרות' };
    }

    try {
      const sortedDates = absencesToDelete.map(a => a.date).sort();
      const rangeStart = sortedDates[0];
      const rangeEnd = sortedDates[sortedDates.length - 1];

      const response = await childCalendarApi.deleteAbsence(firstAbsence.publicId, {
        rangeStart,
        rangeEnd,
      });

      if (response.success) {
        await loadAbsences();
        setSelectedDayInfo(null);
        setEditingAbsenceRange(null);
        return { success: true };
      } else {
        return { success: false, error: response.message };
      }
    } catch (err) {
      console.error('Error deleting absence range:', err);
      return { success: false, error: 'שגיאה במחיקת היעדרויות' };
    }
  };

  // Get all absences for a child (to find related range)
  const getChildAbsenceRange = (absence: Absence): Absence[] => {
    // Find all consecutive absences for this child with the same reason
    const childAbsences = absences
      .filter(a => a.childName === absence.childName && a.reason === absence.reason)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Find the range that includes the clicked absence
    const clickedIndex = childAbsences.findIndex(a => a.date === absence.date);
    if (clickedIndex === -1) return [absence];

    // Find consecutive dates
    const range: Absence[] = [childAbsences[clickedIndex]];

    // Look backward
    for (let i = clickedIndex - 1; i >= 0; i--) {
      const prevDate = new Date(childAbsences[i + 1].date);
      prevDate.setDate(prevDate.getDate() - 1);
      if (childAbsences[i].date === prevDate.toISOString().split('T')[0]) {
        range.unshift(childAbsences[i]);
      } else {
        break;
      }
    }

    // Look forward
    for (let i = clickedIndex + 1; i < childAbsences.length; i++) {
      const nextDate = new Date(childAbsences[i - 1].date);
      nextDate.setDate(nextDate.getDate() + 1);
      if (childAbsences[i].date === nextDate.toISOString().split('T')[0]) {
        range.push(childAbsences[i]);
      } else {
        break;
      }
    }

    return range;
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get days in month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Navigate months
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  // Check if date has absences (can have multiple for different children)
  const getAbsences = (day: number): Absence[] => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return absences.filter(a => a.date === dateStr);
  };

  // Get calendar events for a day
  const getCalendarEventsForDay = (day: number): ParentCalendarEventDto[] => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarEvents.filter(e => e.date === dateStr);
  };

  // Check if today
  const today = new Date();
  const isToday = (day: number) => 
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  // Generate calendar days
  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  // Upcoming absences
  const upcomingAbsences = absences.filter(a => new Date(a.date) >= today).slice(0, 3);

  // Show loading state
  if (isLoading || !childrenLoaded) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
      }}>
        <Loader2 size={32} color={colors.secondary} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: colors.textLight, fontSize: '14px' }}>טוען לוח שנה...</p>
      </div>
    );
  }

  // Show message if no children
  if (children.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
        padding: '20px',
      }}>
        <AlertCircle size={32} color={colors.warning} />
        <p style={{ color: colors.text, fontSize: '16px', textAlign: 'center' }}>לא נמצאו ילדים</p>
        <p style={{ color: colors.textLight, fontSize: '14px', textAlign: 'center' }}>
          נא לוודא שיש ילדים רשומים במערכת
        </p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
        padding: '20px',
      }}>
        <AlertCircle size={32} color={colors.danger} />
        <p style={{ color: colors.danger, fontSize: '14px', textAlign: 'center' }}>{error}</p>
        <motion.button
          onClick={loadAbsences}
          style={{
            padding: '10px 20px',
            backgroundColor: colors.secondary,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
          whileTap={{ scale: 0.95 }}
        >
          נסה שוב
        </motion.button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text }}>
          לוח שנה
        </h1>
        <motion.button
          onClick={() => setShowModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: colors.secondary,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          דיווח היעדרות
        </motion.button>
      </div>

      {/* Calendar Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '20px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        {/* Month Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '8px',
        }}>
          <motion.button
            onClick={nextMonth}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: colors.secondaryLight,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRight size={20} color={colors.secondary} />
          </motion.button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* בחירת חודש */}
            <select
              value={month}
              onChange={(e) => setCurrentDate(new Date(year, parseInt(e.target.value), 1))}
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: colors.text,
                border: `2px solid ${colors.border}`,
                borderRadius: '10px',
                padding: '6px 8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              {hebrewMonths.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>

            {/* בחירת שנה */}
            <select
              value={year}
              onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), month, 1))}
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: colors.text,
                border: `2px solid ${colors.border}`,
                borderRadius: '10px',
                padding: '6px 8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              {Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            {/* כפתור חזרה להיום - מוצג רק כשהחודש הנוכחי שונה מהיום */}
            {(month !== today.getMonth() || year !== today.getFullYear()) && (
              <motion.button
                onClick={() => setCurrentDate(new Date())}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                title="חזור להיום"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  backgroundColor: colors.primaryLight,
                  border: `2px solid ${colors.primary}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                whileTap={{ scale: 0.9 }}
              >
                <RotateCcw size={16} color={colors.primary} />
              </motion.button>
            )}
          </div>

          <motion.button
            onClick={prevMonth}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: colors.secondaryLight,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft size={20} color={colors.secondary} />
          </motion.button>
        </div>

        {/* Day Headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: isDesktop ? '3px' : '2px',
          marginBottom: '4px',
          maxWidth: isDesktop ? '600px' : undefined,
          margin: isDesktop ? '0 auto 4px' : undefined,
        }}>
          {hebrewDays.map((day, index) => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                padding: '4px',
                fontSize: '11px',
                fontWeight: '600',
                color: index === 6 ? colors.primary : colors.textLight,
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(7, 1fr)' : 'repeat(7, 1fr)',
          gap: isDesktop ? '3px' : '2px',
          maxWidth: isDesktop ? '600px' : undefined,
          margin: isDesktop ? '0 auto' : undefined,
        }}>
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} style={{ minHeight: isDesktop ? '80px' : '48px' }} />;
            }

            const dayAbsences = getAbsences(day);
            const dayEvents = getCalendarEventsForDay(day);
            const hasAbsence = dayAbsences.length > 0;
            const hasEvents = dayEvents.length > 0;
            const isSaturday = (index % 7) === 6;
            const todayStyle = isToday(day);
            const isClosed = dayEvents.some(e => e.operationMode === 0);

            // Background color - today, closed days, Saturday
            const getBgColor = () => {
              if (todayStyle && !hasAbsence && !isClosed) return colors.secondary;
              if (isClosed) return '#fef2f2';
              if (isSaturday) return colors.weekend;
              return 'white';
            };

            // Get color for absence tag based on reason
            const getAbsenceColor = (reason: string) => {
              if (reason === 'vacation') return { bg: colors.successLight, text: colors.success };
              if (reason === 'sick') return { bg: colors.dangerLight, text: colors.danger };
              return { bg: colors.warningLight, text: colors.warning };
            };

            return (
              <motion.div
                key={day}
                onClick={() => {
                  if (hasAbsence) {
                    setSelectedDayInfo({ day, absences: dayAbsences });
                  }
                }}
                style={{
                  minHeight: isDesktop ? '80px' : '48px',
                  maxHeight: isDesktop ? '100px' : '65px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  borderRadius: isDesktop ? '6px' : '4px',
                  backgroundColor: getBgColor(),
                  cursor: hasAbsence ? 'pointer' : 'default',
                  position: 'relative',
                  padding: isDesktop ? '4px 2px' : '2px 1px',
                  overflow: 'hidden',
                  border: `1px solid ${colors.border}`,
                }}
                whileHover={{ scale: hasAbsence ? 1.02 : 1 }}
                whileTap={{ scale: hasAbsence ? 0.98 : 1 }}
              >
                <span style={{
                  fontSize: isDesktop ? '13px' : '11px',
                  fontWeight: todayStyle ? '700' : '500',
                  color: todayStyle && !hasAbsence && !isClosed
                    ? 'white'
                    : isSaturday
                      ? colors.warning
                      : colors.text,
                  marginBottom: isDesktop ? '4px' : '2px',
                }}>
                  {day}
                </span>
                {hasEvents && (
                  <div style={{ width: '100%', padding: '0 1px' }}>
                    {dayEvents.slice(0, 1).map((ev, i) => {
                      const evColor = SYSTEM_EVENT_COLORS[ev.eventType] || SYSTEM_EVENT_COLORS[5] || { bg: '#f3f4f6', text: '#6b7280' };
                      return (
                        <div key={`ev-${i}`} style={{
                          fontSize: isDesktop ? '8px' : '6px',
                          fontWeight: '600',
                          color: evColor.text,
                          backgroundColor: evColor.bg,
                          padding: '1px 2px',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                          marginBottom: '1px',
                        }}>
                          {ev.eventName}
                        </div>
                      );
                    })}
                  </div>
                )}
                {hasAbsence && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: isDesktop ? '2px' : '1px',
                    width: '100%',
                    padding: '0 1px',
                  }}>
                    {dayAbsences.slice(0, isDesktop ? 4 : 3).map((absence, i) => {
                      const absColor = getAbsenceColor(absence.reason);
                      return (
                        <div key={i} style={{
                          fontSize: isDesktop ? '9px' : '7px',
                          fontWeight: '600',
                          color: absColor.text,
                          backgroundColor: absColor.bg,
                          padding: isDesktop ? '2px 4px' : '2px 3px',
                          borderRadius: '2px',
                          borderRight: `2px solid ${absColor.text}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: '1.3',
                          textAlign: 'right',
                        }}>
                          {absence.childName}
                        </div>
                      );
                    })}
                    {dayAbsences.length > (isDesktop ? 4 : 3) && (
                      <span style={{ fontSize: isDesktop ? '8px' : '6px', color: colors.textLight, fontWeight: '500', textAlign: 'center' }}>
                        +{dayAbsences.length - (isDesktop ? 4 : 3)}
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: `1px solid ${colors.border}`,
          flexWrap: 'wrap',
        }}>
          <LegendItem color={colors.secondary} filled label="היום" />
          <LegendItem color="#ef4444" label="חג" />
          <LegendItem color="#3b82f6" label="חופשה ארצית" />
          <LegendItem color={colors.success} label="חופשת ילד" />
          <LegendItem color={colors.danger} label="מחלה" />
          <LegendItem color={colors.warning} label="אחר" />
        </div>
      </motion.div>

      {/* Upcoming Events (System Calendar) */}
      {(() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const upcomingEvents = calendarEvents
          .filter(e => e.date >= todayStr)
          .slice(0, 3);
        if (upcomingEvents.length === 0) return null;
        return (
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
              אירועים קרובים
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {upcomingEvents.map((ev, i) => {
                const evColor = SYSTEM_EVENT_COLORS[ev.eventType] || SYSTEM_EVENT_COLORS[5] || { bg: '#f3f4f6', text: '#6b7280', label: 'אחר' };
                const [y, m, d] = ev.date.split('-');
                const dateDisplay = `${d}/${m}/${y}`;
                return (
                  <div key={i} style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    borderRight: `3px solid ${evColor.text}`,
                  }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: evColor.text, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{ev.eventName}</div>
                      <div style={{ fontSize: '12px', color: colors.textLight }}>
                        {dateDisplay} · {evColor.label}
                        {ev.isKindergartenOverride && ev.kindergartenName ? ` · ${ev.kindergartenName}` : ''}
                        {ev.operationMode === 0 ? ' · סגור' : ev.operationMode === 2 ? ` · ${ev.startTime}-${ev.endTime}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Upcoming Absences */}
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
          היעדרויות קרובות
        </h3>
        
        {upcomingAbsences.length === 0 ? (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            padding: '24px',
            textAlign: 'center',
            color: colors.textLight,
          }}>
            <CalendarIcon size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
            <p>אין היעדרויות מתוכננות</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {upcomingAbsences.map((absence, index) => (
              <AbsenceCard key={index} absence={absence} />
            ))}
          </div>
        )}
      </div>

      {/* Add Absence Modal */}
      <AnimatePresence>
        {showModal && <AddAbsenceModal onClose={() => setShowModal(false)} onSave={addAbsence} children={children} />}
      </AnimatePresence>

      {/* Day Details Popup */}
      <AnimatePresence>
        {selectedDayInfo && (
          <DayDetailsPopup
            day={selectedDayInfo.day}
            month={month}
            year={year}
            absences={selectedDayInfo.absences}
            onClose={() => setSelectedDayInfo(null)}
            onEdit={(absence) => {
              const range = getChildAbsenceRange(absence);
              if (range.length > 1) {
                // It's part of a range - open range editor
                setEditingAbsenceRange(range);
              } else {
                // Single day - open simple editor
                setEditingAbsence(absence);
              }
              setSelectedDayInfo(null);
            }}
            onDelete={async (absence) => {
              const range = getChildAbsenceRange(absence);
              if (range.length > 1) {
                // Part of a range - must edit through range editor
                setEditingAbsenceRange(range);
                setSelectedDayInfo(null);
                return { success: true };
              } else {
                // Single day - can delete directly
                return await deleteAbsence(absence);
              }
            }}
            getAbsenceRange={getChildAbsenceRange}
          />
        )}
      </AnimatePresence>

      {/* Edit Single Absence Modal */}
      <AnimatePresence>
        {editingAbsence && (
          <EditAbsenceModal
            absence={editingAbsence}
            onClose={() => setEditingAbsence(null)}
            onSave={updateAbsence}
            onDelete={async () => {
              const result = await deleteAbsence(editingAbsence);
              if (result.success) {
                setEditingAbsence(null);
              }
              return result;
            }}
          />
        )}
      </AnimatePresence>

      {/* Edit Absence Range Modal */}
      <AnimatePresence>
        {editingAbsenceRange && (
          <EditAbsenceRangeModal
            absences={editingAbsenceRange}
            onClose={() => setEditingAbsenceRange(null)}
            onDeleteSingle={async (absence) => {
              const result = await deleteAbsence(absence);
              if (result.success) {
                const remaining = editingAbsenceRange.filter(a => a.date !== absence.date);
                if (remaining.length === 0) {
                  setEditingAbsenceRange(null);
                } else {
                  setEditingAbsenceRange(remaining);
                }
              }
              return result;
            }}
            onDeleteRange={async (start, end) => {
              const toDelete = editingAbsenceRange.filter(a => a.date >= start && a.date <= end);
              const result = await deleteAbsenceRange(toDelete);
              if (result.success) {
                const remaining = editingAbsenceRange.filter(a => a.date < start || a.date > end);
                if (remaining.length === 0) {
                  setEditingAbsenceRange(null);
                } else {
                  setEditingAbsenceRange(remaining);
                }
              }
              return result;
            }}
            onDeleteAll={async () => {
              const result = await deleteAbsenceRange(editingAbsenceRange);
              return result;
            }}
            onUpdateNotes={async (notes) => {
              // Update notes via API - find the first absence ID to update
              const firstAbsence = editingAbsenceRange[0];
              if (!firstAbsence.publicId) {
                return { success: false, error: 'לא נמצא מזהה להיעדרות' };
              }
              try {
                const response = await childCalendarApi.updateAbsence(firstAbsence.publicId, {
                  notes: notes || undefined,
                });
                if (response.success) {
                  await loadAbsences();
                  setEditingAbsenceRange(null);
                  return { success: true };
                }
                return { success: false, error: response.message };
              } catch (err) {
                return { success: false, error: 'שגיאה בעדכון הערות' };
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Legend Item
function LegendItem({ color, label, filled }: { color: string; label: string; filled?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: colors.textLight }}>
      <div style={{
        width: filled ? '16px' : '10px',
        height: filled ? '16px' : '10px',
        borderRadius: filled ? '6px' : '50%',
        backgroundColor: color,
      }} />
      {label}
    </div>
  );
}

// Absence Card
function AbsenceCard({ absence }: { absence: Absence }) {
  // Format date to Israeli format dd/MM/yyyy
  const [year, month, day] = absence.date.split('-');
  const formattedDate = `${day}/${month}/${year}`;

  const getConfig = () => {
    switch (absence.reason) {
      case 'sick':
        return { icon: Thermometer, color: colors.danger, bgColor: colors.dangerLight, label: 'מחלה' };
      case 'vacation':
        return { icon: Plane, color: colors.success, bgColor: colors.successLight, label: 'חופשה' };
      default:
        return { icon: AlertCircle, color: colors.warning, bgColor: colors.warningLight, label: 'אחר' };
    }
  };
  const config = getConfig();

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        backgroundColor: 'white',
        borderRadius: '14px',
        padding: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        borderRight: `4px solid ${config.color}`,
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: config.bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <config.icon size={20} color={config.color} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>{absence.childName}</p>
        <p style={{ fontSize: '13px', color: colors.textLight }}>{formattedDate}</p>
      </div>
      <span style={{
        fontSize: '12px',
        padding: '4px 10px',
        borderRadius: '8px',
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: '500',
      }}>
        {config.label}
      </span>
    </motion.div>
  );
}

// Day Details Popup - shows absence details when clicking on a day
function DayDetailsPopup({
  day,
  month,
  year,
  absences,
  onClose,
  onEdit,
  onDelete,
  getAbsenceRange
}: {
  day: number;
  month: number;
  year: number;
  absences: Absence[];
  getAbsenceRange: (absence: Absence) => Absence[];
  onClose: () => void;
  onEdit: (absence: Absence) => void;
  onDelete: (absence: Absence) => Promise<{ success: boolean; error?: string }>;
}) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async (absence: Absence) => {
    setDeletingId(absence.date + absence.childName);
    setDeleteError(null);
    try {
      const result = await onDelete(absence);
      if (!result.success && result.error) {
        setDeleteError(result.error);
      }
    } catch {
      setDeleteError('שגיאה במחיקת היעדרות');
    } finally {
      setDeletingId(null);
    }
  };
  const formattedDate = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;

  const getReasonConfig = (reason: string) => {
    switch (reason) {
      case 'sick':
        return { icon: Thermometer, color: colors.danger, bgColor: colors.dangerLight, label: 'מחלה' };
      case 'vacation':
        return { icon: Plane, color: colors.success, bgColor: colors.successLight, label: 'חופשה' };
      default:
        return { icon: AlertCircle, color: colors.warning, bgColor: colors.warningLight, label: 'אחר' };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '20px',
          width: '100%',
          maxWidth: '340px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
              פרטי היעדרות
            </h3>
            <p style={{ fontSize: '13px', color: colors.textLight, margin: '4px 0 0 0' }}>
              {formattedDate}
            </p>
          </div>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.border,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={16} color={colors.textLight} />
          </motion.button>
        </div>

        {/* Absences List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {absences.map((absence, index) => {
            const config = getReasonConfig(absence.reason);
            const range = getAbsenceRange(absence);
            const isPartOfRange = range.length > 1;
            const rangeDates = isPartOfRange
              ? `${range[0].date.split('-').reverse().join('/')} - ${range[range.length - 1].date.split('-').reverse().join('/')}`
              : null;

            return (
              <div
                key={index}
                style={{
                  backgroundColor: config.bgColor,
                  borderRadius: '12px',
                  padding: '12px',
                  borderRight: `4px solid ${config.color}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <config.icon size={18} color={config.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
                      {absence.childName}
                    </p>
                    <p style={{ fontSize: '12px', color: config.color, margin: '2px 0 0 0', fontWeight: '500' }}>
                      {config.label}
                    </p>
                    {isPartOfRange && (
                      <p style={{ fontSize: '10px', color: colors.textLight, margin: '2px 0 0 0' }}>
                        חלק מטווח: {rangeDates} ({range.length} ימים)
                      </p>
                    )}
                  </div>
                  {/* Edit & Delete buttons */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <motion.button
                      onClick={() => onEdit(absence)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        border: `1px solid ${colors.border}`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Pencil size={14} color={colors.secondary} />
                    </motion.button>
                    <motion.button
                      onClick={() => handleDelete(absence)}
                      disabled={deletingId !== null}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        border: `1px solid ${colors.border}`,
                        cursor: deletingId !== null ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: deletingId !== null ? 0.5 : 1,
                      }}
                      whileHover={deletingId === null ? { scale: 1.05 } : {}}
                      whileTap={deletingId === null ? { scale: 0.95 } : {}}
                    >
                      {deletingId === (absence.date + absence.childName) ? (
                        <Loader2 size={14} color={colors.danger} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Trash2 size={14} color={colors.danger} />
                      )}
                    </motion.button>
                  </div>
                </div>
                {absence.notes && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    marginTop: '8px',
                  }}>
                    <p style={{ fontSize: '12px', color: colors.textLight, margin: '0 0 2px 0' }}>הערות:</p>
                    <p style={{ fontSize: '13px', color: colors.text, margin: 0 }}>{absence.notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Type for child data
interface ChildOption {
  name: string;
  publicId: string;
  relationType: string;
}

// Check if user can report vacation for a child (only PayingParent, Parent)
const canReportVacation = (relationType: string): boolean => {
  return ['PayingParent', 'Parent'].includes(relationType);
};

// Check if user can report sick leave for a child (any contact can)
const canReportSick = (_relationType: string): boolean => {
  return true; // All contacts can report sick leave
};

// Add Absence Modal
function AddAbsenceModal({
  onClose,
  onSave,
  children
}: {
  onClose: () => void;
  onSave: (absences: Absence[]) => Promise<{ success: boolean; conflicts?: Absence[]; error?: string }>;
  children: ChildOption[];
}) {
  // For vacation - multi-select; for sick - single select
  const [selectedChildren, setSelectedChildren] = useState<ChildOption[]>([]);
  const [selectedReason, setSelectedReason] = useState<'sick' | 'vacation' | 'other' | ''>('');
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ child?: boolean; reason?: boolean; date?: boolean; notes?: boolean }>({});
  const [conflictError, setConflictError] = useState<Absence[] | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Filter children based on reason - vacation requires Parent permission
  const getAvailableChildren = () => {
    if (selectedReason === 'vacation') {
      return children.filter(c => canReportVacation(c.relationType));
    }
    // For sick and other, all children are available
    return children;
  };

  // Check if multi-select is allowed (only for vacation)
  const isMultiSelectMode = selectedReason === 'vacation';

  const reasons = [
    { value: 'sick', label: 'מחלה', icon: Thermometer, color: colors.danger, bgColor: colors.dangerLight },
    { value: 'vacation', label: 'חופשה', icon: Plane, color: colors.secondary, bgColor: colors.secondaryLight },
    { value: 'other', label: 'אחר', icon: AlertCircle, color: colors.warning, bgColor: colors.warningLight },
  ];

  // Handle reason change - reset children selection and filter available children
  const handleReasonChange = (reason: 'sick' | 'vacation' | 'other') => {
    setSelectedReason(reason);
    setErrors(e => ({ ...e, reason: false }));
    // Clear child selection when changing reason (as available children may change)
    setSelectedChildren([]);
  };

  // Handle child selection
  const handleChildToggle = (child: ChildOption) => {
    if (isMultiSelectMode) {
      // Multi-select for vacation
      setSelectedChildren(prev => {
        const isSelected = prev.some(c => c.publicId === child.publicId);
        if (isSelected) {
          return prev.filter(c => c.publicId !== child.publicId);
        } else {
          return [...prev, child];
        }
      });
    } else {
      // Single select for sick/other
      setSelectedChildren([child]);
    }
    setErrors(e => ({ ...e, child: false }));
  };

  // Calculate minimum date - if after child's arrival time (08:30), min is tomorrow
  const getMinDate = () => {
    const now = new Date();
    const arrivalHour = 8;
    const arrivalMinute = 30;

    // If current time is after arrival time, minimum is tomorrow
    if (now.getHours() > arrivalHour || (now.getHours() === arrivalHour && now.getMinutes() >= arrivalMinute)) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    return now.toISOString().split('T')[0];
  };

  const minDate = getMinDate();

  // Format date to Israeli format dd/MM/yyyy
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Generate array of dates between start and end
  const getDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const startD = new Date(start);
    const endD = new Date(end);

    while (startD <= endD) {
      dates.push(startD.toISOString().split('T')[0]);
      startD.setDate(startD.getDate() + 1);
    }
    return dates;
  };

  const handleSave = async () => {
    const newErrors: typeof errors = {};
    setConflictError(null);
    setApiError(null);

    // Validate required fields
    if (selectedChildren.length === 0) newErrors.child = true;
    if (!selectedReason) newErrors.reason = true;
    if (!startDate) newErrors.date = true;
    if (isRangeMode && !endDate) newErrors.date = true;
    // Notes required when reason is 'other'
    if (selectedReason === 'other' && !notes.trim()) newErrors.notes = true;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Create absence entries for each date in range, for each selected child
    const endDateToUse = isRangeMode ? endDate : startDate;
    const dates = getDateRange(startDate, endDateToUse);

    // Create absences for all selected children
    const newAbsences: Absence[] = [];
    for (const child of selectedChildren) {
      for (const date of dates) {
        newAbsences.push({
          date,
          childName: child.name,
          childPublicId: child.publicId,
          reason: selectedReason as 'sick' | 'vacation' | 'other',
          notes: notes || undefined,
        });
      }
    }

    setIsSaving(true);
    try {
      const result = await onSave(newAbsences);
      if (result.success) {
        onClose();
      } else if (result.conflicts) {
        setConflictError(result.conflicts);
      } else if (result.error) {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('שגיאה בשמירת היעדרות');
    } finally {
      setIsSaving(false);
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'sick': return 'מחלה';
      case 'vacation': return 'חופשה';
      default: return 'אחר';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '16px 20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>דיווח היעדרות</h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.border,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.textLight} />
          </motion.button>
        </div>

        {/* Select Reason FIRST - this determines which children are available */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: errors.reason ? colors.danger : colors.text, marginBottom: '6px' }}>
            סיבת ההיעדרות *
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {reasons.map(reason => (
              <motion.button
                key={reason.value}
                onClick={() => handleReasonChange(reason.value as 'sick' | 'vacation' | 'other')}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  borderRadius: '10px',
                  border: `2px solid ${selectedReason === reason.value ? reason.color : errors.reason ? colors.danger : colors.border}`,
                  backgroundColor: selectedReason === reason.value ? reason.bgColor : 'white',
                  color: selectedReason === reason.value ? reason.color : colors.text,
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <reason.icon size={18} />
                {reason.label}
              </motion.button>
            ))}
          </div>
          {errors.reason && <p style={{ fontSize: '11px', color: colors.danger, marginTop: '4px' }}>יש לבחור סיבה</p>}
        </div>

        {/* Select Child - shows after reason is selected */}
        {selectedReason && (
          <div style={{ marginBottom: '12px' }}>
            <p style={{ fontSize: '13px', fontWeight: '500', color: errors.child ? colors.danger : colors.text, marginBottom: '6px' }}>
              {isMultiSelectMode ? 'בחר ילדים (ניתן לבחור מספר ילדים) *' : 'בחר ילד/ה *'}
            </p>
            {getAvailableChildren().length === 0 ? (
              <div style={{
                padding: '12px',
                backgroundColor: colors.warningLight,
                borderRadius: '10px',
                color: colors.warning,
                fontSize: '13px',
                textAlign: 'center'
              }}>
                אין לך הרשאה לדווח חופשה עבור הילדים המקושרים אליך.
                <br />
                רק הורה או הורה משלם יכולים לדווח חופשה.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {getAvailableChildren().map(child => {
                  const isSelected = selectedChildren.some(c => c.publicId === child.publicId);
                  return (
                    <motion.button
                      key={child.publicId}
                      onClick={() => handleChildToggle(child)}
                      style={{
                        flex: getAvailableChildren().length <= 3 ? 1 : 'none',
                        minWidth: getAvailableChildren().length > 3 ? 'calc(50% - 4px)' : undefined,
                        padding: '10px',
                        borderRadius: '10px',
                        border: `2px solid ${isSelected ? colors.primary : errors.child ? colors.danger : colors.border}`,
                        backgroundColor: isSelected ? colors.primaryLight : 'white',
                        color: isSelected ? colors.primary : colors.text,
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isMultiSelectMode && (
                        <span style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          border: `2px solid ${isSelected ? colors.primary : colors.border}`,
                          backgroundColor: isSelected ? colors.primary : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                        }}>
                          {isSelected && '✓'}
                        </span>
                      )}
                      {child.name}
                    </motion.button>
                  );
                })}
              </div>
            )}
            {errors.child && <p style={{ fontSize: '11px', color: colors.danger, marginTop: '4px' }}>יש לבחור ילד/ה</p>}
            {isMultiSelectMode && selectedChildren.length > 1 && (
              <p style={{ fontSize: '11px', color: colors.secondary, marginTop: '4px' }}>
                נבחרו {selectedChildren.length} ילדים
              </p>
            )}
          </div>
        )}

        {/* Date Mode Toggle */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>סוג היעדרות</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <motion.button
              onClick={() => setIsRangeMode(false)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: `2px solid ${!isRangeMode ? colors.secondary : colors.border}`,
                backgroundColor: !isRangeMode ? colors.secondaryLight : 'white',
                color: !isRangeMode ? colors.secondary : colors.text,
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              יום אחד
            </motion.button>
            <motion.button
              onClick={() => setIsRangeMode(true)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '10px',
                border: `2px solid ${isRangeMode ? colors.secondary : colors.border}`,
                backgroundColor: isRangeMode ? colors.secondaryLight : 'white',
                color: isRangeMode ? colors.secondary : colors.text,
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              טווח תאריכים
            </motion.button>
          </div>
        </div>

        {/* Date Selection - Both dates in same row */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>
                {isRangeMode ? 'מתאריך' : 'תאריך'}
              </p>
              <DateInput
                value={startDate}
                onChange={setStartDate}
                min={minDate}
              />
            </div>
            {isRangeMode && (
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>עד תאריך</p>
                <DateInput
                  value={endDate}
                  onChange={setEndDate}
                  min={startDate || minDate}
                />
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{
            fontSize: '13px',
            fontWeight: '500',
            color: errors.notes ? colors.danger : colors.text,
            marginBottom: '6px'
          }}>
            הערות {selectedReason === 'other' ? '*' : '(אופציונלי)'}
          </p>
          <input
            type="text"
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setErrors(err => ({ ...err, notes: false })); }}
            placeholder={selectedReason === 'other' ? 'חובה להזין סיבה...' : 'הוסף הערה...'}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '10px',
              border: `2px solid ${errors.notes ? colors.danger : colors.border}`,
              fontSize: '14px',
              color: colors.text,
              backgroundColor: 'white',
              boxSizing: 'border-box',
            }}
          />
          {errors.notes && <p style={{ fontSize: '11px', color: colors.danger, marginTop: '4px' }}>יש להזין סיבה</p>}
        </div>

        {/* Summary */}
        {startDate && (
          <div style={{
            backgroundColor: colors.secondaryLight,
            borderRadius: '10px',
            padding: '10px 12px',
            marginBottom: '12px',
          }}>
            <p style={{ fontSize: '13px', color: colors.secondary, fontWeight: '500' }}>
              {isRangeMode && endDate
                ? `מ-${formatDateDisplay(startDate)} עד ${formatDateDisplay(endDate)}`
                : formatDateDisplay(startDate)
              }
            </p>
          </div>
        )}

        {/* Conflict Error */}
        {conflictError && (
          <div style={{
            backgroundColor: colors.dangerLight,
            borderRadius: '10px',
            padding: '12px',
            marginBottom: '12px',
            borderRight: `4px solid ${colors.danger}`,
          }}>
            <p style={{ fontSize: '13px', color: colors.danger, fontWeight: '600', margin: '0 0 6px 0' }}>
              לא ניתן להוסיף היעדרות
            </p>
            <p style={{ fontSize: '12px', color: colors.text, margin: 0 }}>
              כבר קיימת היעדרות בתאריכים הבאים:
            </p>
            <ul style={{ margin: '6px 0 0 0', paddingRight: '16px', fontSize: '12px', color: colors.textLight }}>
              {conflictError.map((c, i) => (
                <li key={i}>
                  {formatDateDisplay(c.date)} - {getReasonLabel(c.reason)}
                </li>
              ))}
            </ul>
            <p style={{ fontSize: '11px', color: colors.textLight, margin: '8px 0 0 0' }}>
              כדי לשנות היעדרות קיימת, לחץ על היום בלוח השנה ובחר &quot;ערוך&quot;
            </p>
          </div>
        )}

        {/* API Error */}
        {apiError && (
          <div style={{
            marginBottom: '12px',
            padding: '10px 12px',
            backgroundColor: colors.dangerLight,
            borderRadius: '8px',
            border: `1px solid ${colors.danger}`,
          }}>
            <p style={{ fontSize: '13px', color: colors.danger, fontWeight: '500' }}>
              {apiError}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <motion.button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '12px',
            background: isSaving ? colors.textLight : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          whileTap={isSaving ? {} : { scale: 0.98 }}
        >
          {isSaving && <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />}
          {isSaving ? 'שומר...' : 'שמור היעדרות'}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// Edit Absence Modal
function EditAbsenceModal({
  absence,
  onClose,
  onSave,
  onDelete
}: {
  absence: Absence;
  onClose: () => void;
  onSave: (original: Absence, updated: Absence) => Promise<{ success: boolean; error?: string }>;
  onDelete: () => Promise<{ success: boolean; error?: string }>;
}) {
  const [selectedReason, setSelectedReason] = useState<'sick' | 'vacation' | 'other'>(absence.reason);
  const [date, setDate] = useState(absence.date);
  const [notes, setNotes] = useState(absence.notes || '');
  const [errors, setErrors] = useState<{ date?: boolean; notes?: boolean }>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const reasons = [
    { value: 'sick', label: 'מחלה', icon: Thermometer, color: colors.danger, bgColor: colors.dangerLight },
    { value: 'vacation', label: 'חופשה', icon: Plane, color: colors.success, bgColor: colors.successLight },
    { value: 'other', label: 'אחר', icon: AlertCircle, color: colors.warning, bgColor: colors.warningLight },
  ];

  // Format date to Israeli format dd/MM/yyyy
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleSave = async () => {
    const newErrors: typeof errors = {};
    setApiError(null);

    if (!date) newErrors.date = true;
    if (selectedReason === 'other' && !notes.trim()) newErrors.notes = true;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await onSave(absence, {
        ...absence,
        date,
        reason: selectedReason,
        notes: notes || undefined,
      });

      if (!result.success && result.error) {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('שגיאה בעדכון היעדרות');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      const result = await onDelete();
      if (!result.success && result.error) {
        setApiError(result.error);
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      setApiError('שגיאה במחיקת היעדרות');
      setShowDeleteConfirm(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (showDeleteConfirm) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '24px',
            width: '100%',
            maxWidth: '320px',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: colors.dangerLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Trash2 size={28} color={colors.danger} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
            מחיקת היעדרות
          </h3>
          <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0' }}>
            האם אתה בטוח שברצונך למחוק את ההיעדרות של {absence.childName} בתאריך {formatDateDisplay(absence.date)}?
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <motion.button
              autoFocus
              onClick={() => setShowDeleteConfirm(false)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: `2px solid ${colors.border}`,
                backgroundColor: 'white',
                color: colors.text,
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              ביטול
            </motion.button>
            <motion.button
              onClick={handleDelete}
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: isSaving ? colors.textLight : colors.danger,
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
              whileTap={isSaving ? {} : { scale: 0.98 }}
            >
              {isSaving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {isSaving ? 'מוחק...' : 'מחק'}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '16px 20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>עריכת היעדרות</h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.border,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.textLight} />
          </motion.button>
        </div>

        {/* Child Name (read-only) */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>
            ילד/ה
          </p>
          <div style={{
            padding: '10px',
            borderRadius: '10px',
            border: `2px solid ${colors.border}`,
            backgroundColor: colors.border,
            color: colors.text,
            fontSize: '14px',
            fontWeight: '500',
          }}>
            {absence.childName}
          </div>
        </div>

        {/* Select Reason */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>
            סיבת ההיעדרות
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {reasons.map(reason => (
              <motion.button
                key={reason.value}
                onClick={() => setSelectedReason(reason.value as 'sick' | 'vacation' | 'other')}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  borderRadius: '10px',
                  border: `2px solid ${selectedReason === reason.value ? reason.color : colors.border}`,
                  backgroundColor: selectedReason === reason.value ? reason.bgColor : 'white',
                  color: selectedReason === reason.value ? reason.color : colors.text,
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <reason.icon size={18} />
                {reason.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Date Selection */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: errors.date ? colors.danger : colors.text, marginBottom: '6px' }}>
            תאריך
          </p>
          <DateInput
            value={date}
            onChange={(v) => { setDate(v); setErrors(err => ({ ...err, date: false })); }}
            borderColor={errors.date ? colors.danger : undefined}
          />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{
            fontSize: '13px',
            fontWeight: '500',
            color: errors.notes ? colors.danger : colors.text,
            marginBottom: '6px'
          }}>
            הערות {selectedReason === 'other' ? '*' : '(אופציונלי)'}
          </p>
          <input
            type="text"
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setErrors(err => ({ ...err, notes: false })); }}
            placeholder={selectedReason === 'other' ? 'חובה להזין סיבה...' : 'הוסף הערה...'}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '10px',
              border: `2px solid ${errors.notes ? colors.danger : colors.border}`,
              fontSize: '14px',
              color: colors.text,
              backgroundColor: 'white',
              boxSizing: 'border-box',
            }}
          />
          {errors.notes && <p style={{ fontSize: '11px', color: colors.danger, marginTop: '4px' }}>יש להזין סיבה</p>}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <motion.button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '12px',
              border: `2px solid ${colors.danger}`,
              backgroundColor: 'white',
              color: colors.danger,
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            whileTap={{ scale: 0.98 }}
          >
            <Trash2 size={18} />
            מחק
          </motion.button>
          <motion.button
            onClick={handleSave}
            style={{
              flex: 2,
              padding: '14px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            שמור שינויים
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Edit Absence Range Modal - for editing/deleting a range of absences
function EditAbsenceRangeModal({
  absences,
  onClose,
  onDeleteSingle,
  onDeleteRange,
  onDeleteAll,
  onUpdateNotes
}: {
  absences: Absence[];
  onClose: () => void;
  onDeleteSingle: (absence: Absence) => Promise<{ success: boolean; error?: string }>;
  onDeleteRange: (startDate: string, endDate: string) => Promise<{ success: boolean; error?: string }>;
  onDeleteAll: () => Promise<{ success: boolean; error?: string }>;
  onUpdateNotes: (notes: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const [deleteMode, setDeleteMode] = useState<'none' | 'single' | 'range' | 'all'>('none');
  const [selectedDate, setSelectedDate] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [notes, setNotes] = useState(absences[0]?.notes || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const sortedAbsences = [...absences].sort((a, b) => a.date.localeCompare(b.date));
  const firstDate = sortedAbsences[0]?.date || '';
  const lastDate = sortedAbsences[sortedAbsences.length - 1]?.date || '';
  const childName = absences[0]?.childName || '';
  const reason = absences[0]?.reason || 'other';

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getReasonConfig = () => {
    switch (reason) {
      case 'sick':
        return { icon: Thermometer, color: colors.danger, bgColor: colors.dangerLight, label: 'מחלה' };
      case 'vacation':
        return { icon: Plane, color: colors.success, bgColor: colors.successLight, label: 'חופשה' };
      default:
        return { icon: AlertCircle, color: colors.warning, bgColor: colors.warningLight, label: 'אחר' };
    }
  };

  const config = getReasonConfig();

  const handleDelete = async () => {
    setIsSaving(true);
    setApiError(null);

    try {
      let result: { success: boolean; error?: string };

      if (deleteMode === 'single' && selectedDate) {
        const toDelete = absences.find(a => a.date === selectedDate);
        if (toDelete) {
          result = await onDeleteSingle(toDelete);
        } else {
          result = { success: false, error: 'לא נמצאה היעדרות' };
        }
      } else if (deleteMode === 'range' && rangeStart && rangeEnd) {
        result = await onDeleteRange(rangeStart, rangeEnd);
      } else if (deleteMode === 'all') {
        result = await onDeleteAll();
      } else {
        result = { success: false, error: 'לא נבחרה פעולה' };
      }

      if (!result.success && result.error) {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('שגיאה במחיקת היעדרויות');
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
      setDeleteMode('none');
    }
  };

  const handleSaveNotes = async () => {
    setIsSaving(true);
    setApiError(null);

    try {
      const result = await onUpdateNotes(notes);
      if (!result.success && result.error) {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('שגיאה בעדכון הערות');
    } finally {
      setIsSaving(false);
    }
  };

  if (showDeleteConfirm) {
    let confirmMessage = '';
    if (deleteMode === 'single') {
      confirmMessage = `האם למחוק את ההיעדרות בתאריך ${formatDateDisplay(selectedDate)}?`;
    } else if (deleteMode === 'range') {
      confirmMessage = `האם למחוק את ההיעדרויות מ-${formatDateDisplay(rangeStart)} עד ${formatDateDisplay(rangeEnd)}?`;
    } else if (deleteMode === 'all') {
      confirmMessage = `האם למחוק את כל ההיעדרויות (${absences.length} ימים)?`;
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '24px',
            width: '100%',
            maxWidth: '320px',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: colors.dangerLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Trash2 size={28} color={colors.danger} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
            אישור מחיקה
          </h3>
          <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0' }}>
            {confirmMessage}
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <motion.button
              autoFocus
              onClick={() => setShowDeleteConfirm(false)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: `2px solid ${colors.border}`,
                backgroundColor: 'white',
                color: colors.text,
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              ביטול
            </motion.button>
            <motion.button
              onClick={handleDelete}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: colors.danger,
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              מחק
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '16px 20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>עריכת טווח היעדרויות</h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.border,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.textLight} />
          </motion.button>
        </div>

        {/* Range Info */}
        <div style={{
          backgroundColor: config.bgColor,
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '16px',
          borderRight: `4px solid ${config.color}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <config.icon size={20} color={config.color} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '15px', fontWeight: '600', color: colors.text, margin: 0 }}>
                {childName}
              </p>
              <p style={{ fontSize: '13px', color: config.color, margin: '2px 0 0 0', fontWeight: '500' }}>
                {config.label}
              </p>
              <p style={{ fontSize: '12px', color: colors.textLight, margin: '4px 0 0 0' }}>
                {formatDateDisplay(firstDate)} - {formatDateDisplay(lastDate)} ({absences.length} ימים)
              </p>
            </div>
          </div>
        </div>

        {/* Delete Options */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '10px' }}>
            מחיקת היעדרויות
          </p>

          {/* Delete Single Day */}
          <motion.button
            onClick={() => setDeleteMode(deleteMode === 'single' ? 'none' : 'single')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '10px',
              border: `2px solid ${deleteMode === 'single' ? colors.danger : colors.border}`,
              backgroundColor: deleteMode === 'single' ? colors.dangerLight : 'white',
              color: deleteMode === 'single' ? colors.danger : colors.text,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              textAlign: 'right',
              marginBottom: '8px',
            }}
            whileTap={{ scale: 0.98 }}
          >
            מחק יום בודד
          </motion.button>

          {deleteMode === 'single' && (
            <div style={{ marginBottom: '8px', paddingRight: '12px' }}>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  fontSize: '13px',
                  color: colors.text,
                  backgroundColor: 'white',
                }}
              >
                <option value="">בחר תאריך</option>
                {sortedAbsences.map(a => (
                  <option key={a.date} value={a.date}>
                    {formatDateDisplay(a.date)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Delete Range */}
          <motion.button
            onClick={() => setDeleteMode(deleteMode === 'range' ? 'none' : 'range')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '10px',
              border: `2px solid ${deleteMode === 'range' ? colors.danger : colors.border}`,
              backgroundColor: deleteMode === 'range' ? colors.dangerLight : 'white',
              color: deleteMode === 'range' ? colors.danger : colors.text,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              textAlign: 'right',
              marginBottom: '8px',
            }}
            whileTap={{ scale: 0.98 }}
          >
            מחק טווח תאריכים
          </motion.button>

          {deleteMode === 'range' && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', paddingRight: '12px' }}>
              <select
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  fontSize: '13px',
                  color: colors.text,
                  backgroundColor: 'white',
                }}
              >
                <option value="">מתאריך</option>
                {sortedAbsences.map(a => (
                  <option key={a.date} value={a.date}>
                    {formatDateDisplay(a.date)}
                  </option>
                ))}
              </select>
              <select
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  fontSize: '13px',
                  color: colors.text,
                  backgroundColor: 'white',
                }}
              >
                <option value="">עד תאריך</option>
                {sortedAbsences.filter(a => a.date >= rangeStart).map(a => (
                  <option key={a.date} value={a.date}>
                    {formatDateDisplay(a.date)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Delete All */}
          <motion.button
            onClick={() => setDeleteMode(deleteMode === 'all' ? 'none' : 'all')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '10px',
              border: `2px solid ${deleteMode === 'all' ? colors.danger : colors.border}`,
              backgroundColor: deleteMode === 'all' ? colors.dangerLight : 'white',
              color: deleteMode === 'all' ? colors.danger : colors.text,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              textAlign: 'right',
            }}
            whileTap={{ scale: 0.98 }}
          >
            מחק את כל הטווח ({absences.length} ימים)
          </motion.button>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>
            הערות (אופציונלי)
          </p>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="הוסף או ערוך הערה..."
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '10px',
              border: `2px solid ${colors.border}`,
              fontSize: '14px',
              color: colors.text,
              backgroundColor: 'white',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          {deleteMode !== 'none' && (
            <motion.button
              onClick={() => {
                if (deleteMode === 'single' && !selectedDate) return;
                if (deleteMode === 'range' && (!rangeStart || !rangeEnd)) return;
                setShowDeleteConfirm(true);
              }}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: colors.danger,
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                opacity: (deleteMode === 'single' && !selectedDate) || (deleteMode === 'range' && (!rangeStart || !rangeEnd)) ? 0.5 : 1,
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Trash2 size={18} />
              מחק
            </motion.button>
          )}
          <motion.button
            onClick={() => {
              if (notes !== absences[0]?.notes) {
                onUpdateNotes(notes);
              } else {
                onClose();
              }
            }}
            style={{
              flex: deleteMode !== 'none' ? 1 : 2,
              padding: '14px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            {notes !== absences[0]?.notes ? 'שמור הערות' : 'סגור'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}